// components/AddLeadsDetails.jsx
import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    Stack,
    IconButton,
    Typography,
    MenuItem,
    InputLabel,
    Select,
    FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import Utils from '../../core/Utils';
import moment from 'moment';
import { BusinessCategories, LeadStatus } from '../../core/Constants';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import CountryHelper from '../../core/CountryHelper';
import { getCountry } from '../../data/localSettingsActions';
import 'moment/locale/fr';
import BusinessHelper from '../../core/BusinessHelper';
import NumericTextField from '../common/controls/NumericTextField';

const AddLeadsDetails = ({ open, onClose, onSubmit, initialValues = {}, followUpOnly = false, userList = [] }) => {
    const [customerName, setCustomerName] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    const [customerMobile, setCustomerMobile] = useState('');
    const [vehicleMake, setVehicleMake] = useState('');
    const [vehicleModel, setVehicleModel] = useState('');
    const [leadStatus, setLeadStatus] = useState("Open Lead");
    const [leadSource, setLeadSource] = useState('');
    const [comments, setComments] = useState('');
    const [followupInfo, setFollowupInfo] = useState([
        { followupDate: '', comment: '', nextFollowupDT: '' },
    ]);
    const [leadStatusError, setLeadStatusError] = useState(false);
    const [selectedUser, setSelectedUser] = useState('');


    useEffect(() => {
        setCustomerName(initialValues?.customerName || '');
        setCustomerAddress(initialValues?.address || '');
        setCustomerMobile(initialValues?.mobileNumber || '');
        setVehicleMake(initialValues?.make || '');
        setVehicleModel(initialValues?.model || '');
        setLeadStatus(initialValues?.status || 'Open Lead');
        setLeadSource(initialValues?.source || '');
        setComments(initialValues?.comments || '');
        setFollowupInfo(
            Array.isArray(initialValues?.followupInfo) && initialValues.followupInfo.length > 0
                ? initialValues.followupInfo
                : [{ followupDate: moment(), comment: '', nextFollowupDT: moment() }]
        );
        const exists = userList.some((u) => u.id === initialValues?.selectedUserId);
        if (exists) {
        setSelectedUser(initialValues?.selectedUserId);
        } else {
            setSelectedUser("");
        }
    }, [initialValues, open]);

    const handleCommitChange = (index, key, value) => {
        const updatedList = [...followupInfo];
        updatedList[index] = {
            ...updatedList[index],
            [key]: value,
        };
        setFollowupInfo(updatedList);
    };

    const handleAddFollowUp = () => {
        if (followupInfo.length < 5) {
            setFollowupInfo([
                ...followupInfo,
                { followupDate: moment(), comment: '', nextFollowupDT: moment() },
            ]);
        }
    };

    const handleRemoveFollowUp = (index) => {
        const updated = followupInfo.filter((_, i) => i !== index);
        setFollowupInfo(updated);
    };

    const handleSave = () => {
        if (!leadStatus) {
            setLeadStatusError(true);
            return;
        } else {
            setLeadStatusError(false);
        }
        if (!customerName.trim()) {
            return;
        }
        if (!Utils.IsMobileValid(customerMobile)) {
            return;
        }
        if (!customerMobile.trim()) {
            return;
        }
        const formData = {
            customerName,
            address: customerAddress,
            mobileNumber: customerMobile,
            make: vehicleMake,
            model: vehicleModel,
            status: leadStatus,
            source: leadSource,
            comments,
            followupInfo,
            selectedUserId: selectedUser || "",
        };

        onSubmit(formData);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Add Lead Details</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                <fieldset disabled={followUpOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
                    <Stack spacing={2} className="DialogContent font-size-14">
                        <FormControl fullWidth>
                            <TextField
                                label="Customer Name"
                                variant="outlined"
                                value={customerName}
                                error={!customerName}
                                onChange={(e) => setCustomerName(e.target.value)}
                                required
                            />
                        </FormControl>

                        <FormControl fullWidth>
                            <TextField
                                label="Customer Address"
                                variant="outlined"
                                value={customerAddress}
                                onChange={(e) => setCustomerAddress(e.target.value)}
                                multiline
                                rows={2}
                            />
                        </FormControl>

                        <FormControl fullWidth>
                            <NumericTextField
                                label="Mobile Number"
                                variant="outlined"
                                value={customerMobile}
                                error={!Utils.IsMobileValid(customerMobile)}
                                setValue={setCustomerMobile}
                                inputProps={{
                                    maxLength: CountryHelper.GetMobileNumberMaxLength(),
                                }}
                                required
                            />
                        </FormControl>
                        <FormControl fullWidth>
                        <InputLabel>Select User</InputLabel>
                        <Select
                            label="Select User"
                            value={selectedUser}
                            disabled={followUpOnly}
                            onChange={(e) => {
                                setSelectedUser(e.target.value);
                            }}
                        >
                            {userList
                                .filter(user => user.role !== "GlobalAdmin" && user.role !== "BranchAdmin")
                                .map((user) => (
                                    <MenuItem key={user.id} value={String(user.id)}>
                                    {user.firstName ?? ""}
                                    </MenuItem>
                                ))}
                        </Select>
                        </FormControl>
                        {BusinessHelper.GetBusinessCategory() === BusinessCategories.Automobile && (
                            <Stack direction="row" spacing={2}>
                                <FormControl fullWidth>
                                    <TextField
                                        label="Vehicle Make"
                                        variant="outlined"
                                        value={vehicleMake}
                                        onChange={(e) => setVehicleMake(e.target.value)}
                                    />
                                </FormControl>

                                <FormControl fullWidth>
                                    <TextField
                                        label="Vehicle Model"
                                        variant="outlined"
                                        value={vehicleModel}
                                        onChange={(e) => setVehicleModel(e.target.value)}
                                    />
                                </FormControl>
                            </Stack>
                        )}

                        <Stack direction="row" spacing={2}>
                            <FormControl fullWidth error={leadStatusError}>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    label="Status"
                                    value={leadStatus}
                                    disabled={followUpOnly}
                                    onChange={(e) => {
                                        setLeadStatus(e.target.value);
                                        setLeadStatusError(false);
                                    }}
                                    required
                                >
                                    {Object.entries(LeadStatus).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>
                                            {value}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {leadStatusError && (
                                    <FormHelperText>Status is required</FormHelperText>
                                )}
                            </FormControl>


                            <FormControl fullWidth>
                                <TextField
                                    label="Source"
                                    variant="outlined"
                                    value={leadSource}
                                    onChange={(e) => setLeadSource(e.target.value)}
                                />
                            </FormControl>
                        </Stack>

                        <FormControl fullWidth>
                            <TextField
                                label="Comments"
                                variant="outlined"
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                multiline
                                rows={2}
                            />
                        </FormControl>
                    </Stack>

                    <Stack className="font-size-12" sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" style={{ margin: '10px' }}>
                            Follow-Up Details (Max 5)
                        </Typography>

                        {!Utils.IsNullOrEmptyArray(followupInfo) && followupInfo.map((commit, index) => (
                            <Stack direction="row" spacing={2} key={index} sx={{ mb: 2 }} alignItems="center">
                                <LocalizationProvider dateAdapter={AdapterMoment} >
                                    <DatePicker
                                        label="Followup Date"
                                        format='DD/MM/YYYY'
                                        InputLabelProps={{ shrink: true }}
                                        onChange={(newValue) => handleCommitChange(index, 'followupDate', newValue)}
                                        value={commit.followupDate ? moment(commit.followupDate) : ''}
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </LocalizationProvider>
                                <TextField
                                    label="Comments"
                                    value={commit.comment}
                                    onChange={(e) => handleCommitChange(index, 'comment', e.target.value)}
                                    fullWidth
                                />
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker
                                        label="Next Followup Date"
                                        value={commit.nextFollowupDT ? moment(commit.nextFollowupDT) : null}
                                        onChange={(newValue) => handleCommitChange(index, 'nextFollowupDT', newValue)}
                                        disablePast
                                        format='DD/MM/YYYY'
                                        maxDate={moment().add(6, 'months')}
                                        minDate={moment()}
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </LocalizationProvider>
                                {followupInfo.length > 1 && (
                                    <IconButton color="error" onClick={() => handleRemoveFollowUp(index)}>
                                        <CloseIcon />
                                    </IconButton>
                                )}
                            </Stack>
                        ))}
                        {!followUpOnly && (
                            <>
                                {followupInfo.length < 5 && (
                                    <Button variant="outlined" className="btn btn-primary" onClick={handleAddFollowUp}>
                                        Add Follow-Up
                                    </Button>
                                )}
                            </>
                        )}
                    </Stack>
                </fieldset>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button variant="contained"
                    className="btn btn-secondary" onClick={onClose}>
                    Cancel
                </Button>
                {!followUpOnly && (
                    <Button variant="contained"
                        className="btn btn-primary" onClick={handleSave}>
                        Save
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default AddLeadsDetails;
